import {useEffect, useCallback, useMemo, useState} from 'react';

import type {Widget} from 'shells/widgets/types';

import {
    mapGraphQLWidgetToClientWidget,
    mapClientWidgetToGraphQLWidget,
} from '../../../core-entity/layout-customizer/helpers';
import {LayoutCustomizer} from '../../../core-entity/layout-customizer/layout-customizer';
import type {UpdateWidgetLayoutInput} from '../../../core-entity/layout-customizer/layout-customizer';

import {analyticsClient} from '../../../../analytics-client';

import {useGetStagesetWidgets} from './graphql/hooks/use-get-stageset-widgets';
import {useUpdateWidgetStageset} from './graphql/hooks/use-update-widget-stageset';
import {LearnMoreModal} from './learn-more-modal';

interface Props {
    stagesetId: string;
}

export const LeadLayoutCustomizer = (props: Props) => {
    const {stageset, isLoading} = useGetStagesetWidgets(props.stagesetId);
    const {updateWidgetStageset, isLoading: isSaving} = useUpdateWidgetStageset();
    const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = useState<boolean>(false);

    const sidebarWidgets = useMemo<Widget[]>(
        () => (stageset?.sidebarWidgets ?? []).map(mapGraphQLWidgetToClientWidget),
        [stageset?.sidebarWidgets]
    );
    const workspaceWidgets = useMemo<Widget[]>(
        () => (stageset?.workspaceWidgets ?? []).map(mapGraphQLWidgetToClientWidget),
        [stageset?.workspaceWidgets]
    );

    const handleSave = useCallback(
        (input: UpdateWidgetLayoutInput) => {
            updateWidgetStageset({
                stageset: props.stagesetId,
                sidebar: input.sidebar.map((w) => mapClientWidgetToGraphQLWidget(w)),
                workspace: input.workspace.map((w) => mapClientWidgetToGraphQLWidget(w)),
            });
        },
        [props.stagesetId, updateWidgetStageset]
    );

    // Track an analytics event when a user views the layout customizer
    useEffect(() => {
        analyticsClient.track(
            analyticsClient.EVENTS.TRACK_LEAD_PAGE_WIDGET_LAYOUT_CUSTOMIZER_VIEWED
        );
    }, []);

    return (
        <>
            <LayoutCustomizer
                initialSidebarWidgets={sidebarWidgets}
                initialWorkspaceWidgets={workspaceWidgets}
                updateWidgetLayout={handleSave}
                isSaving={isSaving}
                isLoading={isLoading}
                subtitle={stageset && stageset.name ? `Customizing ${stageset.name}` : undefined}
                cueCardProps={{
                    title: 'Lead layout customization',
                    description:
                        'Design personalized widgets to gather essential data for your pipelines, and organize them alongside other tools in a way that suits your workflow.',
                    illustrationString: 'lead-customize-layout-simple',
                    illustrationWidth: 150,
                    ctaOnClick: () => setIsLearnMoreModalOpen(true),
                }}
                entityType='LEAD'
            />
            {isLearnMoreModalOpen && <LearnMoreModal setIsOpen={setIsLearnMoreModalOpen} />}
        </>
    );
};
